import type { StatementSummary } from "@paredown/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, putToS3, type Api } from "../api.js";
import { redactPdf } from "../redact.js";
import { labelFor, setNickname } from "../vault.js";

interface Job {
  name: string;
  stage: "redacting" | "uploading" | "registering" | "done" | "duplicate" | "error";
  detail?: string;
}

const STATUS_LABEL: Record<StatementSummary["status"], string> = {
  queued: "Waiting",
  extracting: "Reading",
  needs_review: "Needs a look",
  confirmed: "Confirmed",
  failed: "Could not read",
};

export function Statements({ api }: { api: Api }) {
  const [list, setList] = useState<StatementSummary[] | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [approving, setApproving] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const refresh = useCallback(() => {
    api
      .listStatements()
      .then(setList)
      .catch((e: Error) => setError(e.message));
  }, [api]);

  useEffect(refresh, [refresh]);
  // Poll while anything is still in flight.
  useEffect(() => {
    if (!list?.some((s) => s.status === "queued" || s.status === "extracting")) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [list, refresh]);

  // Drop selections for rows that no longer exist.
  useEffect(() => {
    if (!list) return;
    const ids = new Set(list.map((s) => s.id));
    setSelected((sel) => ([...sel].every((id) => ids.has(id)) ? sel : new Set([...sel].filter((id) => ids.has(id)))));
  }, [list]);

  const toggle = (id: string, on: boolean) =>
    setSelected((sel) => {
      const next = new Set(sel);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  // Statements whose checks pass are confirmed by the server; only the rest need the user.
  const attention = list?.filter((s) => s.status !== "confirmed") ?? [];
  const visible = showAll ? (list ?? []) : attention;
  const cleanWaiting = attention.filter((s) => s.status === "needs_review" && !s.problemCount).length;
  const allSelected = !!visible.length && visible.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visible.map((s) => s.id)));

  async function approveClean() {
    setApproving(true);
    try {
      await api.confirmClean();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setApproving(false);
    refresh();
  }

  async function deleteSelected() {
    const ids = visible.filter((s) => selected.has(s.id)).map((s) => s.id);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} statement${ids.length === 1 ? "" : "s"}?`)) return;
    setDeleting(true);
    const results = await Promise.allSettled(ids.map((id) => api.remove(id)));
    const failed = results.filter((r) => r.status === "rejected").length;
    setDeleting(false);
    if (failed) setError(`${failed} of ${ids.length} could not be deleted.`);
    refresh();
  }

  const update = (i: number, patch: Partial<Job>) =>
    setJobs((js) => js.map((j, k) => (k === i ? { ...j, ...patch } : j)));

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    const start = jobs.length;
    const picked = [...files];
    setJobs((js) => [...js, ...picked.map((f) => ({ name: f.name, stage: "redacting" as const }))]);
    for (const [k, file] of picked.entries()) {
      const i = start + k;
      try {
        const red = await redactPdf(file, (p, n) => update(i, { detail: `page ${p} of ${n}` }));
        update(i, { stage: "uploading", detail: `${red.found} number${red.found === 1 ? "" : "s"} redacted` });
        const { key, url } = await api.uploadUrl(file.name);
        await putToS3(url, red.pdf);
        update(i, { stage: "registering" });
        await api.register({
          key,
          fileName: file.name,
          contentHash: red.contentHash,
          redactedText: red.redactedText,
          tokens: red.tokens,
          pageCount: red.pageCount,
        });
        update(i, { stage: "done" });
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) update(i, { stage: "duplicate", detail: "already uploaded" });
        else update(i, { stage: "error", detail: e instanceof Error ? e.message : String(e) });
      }
    }
    refresh();
  }

  return (
    <section>
      <h2>Bring in statements</h2>
      <p>
        Drop PDF statements from any bank or card. Account numbers are blacked out in your browser before anything is
        uploaded; the original file never leaves this device.
      </p>
      <div
        className="drop"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void handleFiles(e.dataTransfer.files);
        }}
        onClick={() => input.current?.click()}
        role="button"
        tabIndex={0}
        data-testid="drop-zone"
      >
        Drop PDFs here or tap to choose
        <input
          ref={input}
          type="file"
          accept="application/pdf"
          multiple
          hidden
          onChange={(e) => void handleFiles(e.target.files)}
          data-testid="file-input"
        />
      </div>

      {jobs.length > 0 && (
        <ul className="jobs">
          {jobs.map((j, i) => (
            <li key={i} className={`job ${j.stage}`}>
              <span>{j.name}</span>
              <span>
                {j.stage}
                {j.detail ? ` · ${j.detail}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}

      <h2>Your statements</h2>
      {error && <p className="error">{error}</p>}
      {list === null ? (
        <p>Loading…</p>
      ) : list.length === 0 ? (
        <p>None yet.</p>
      ) : (
        <>
          <p data-testid="statements-summary">
            {list.length - attention.length} of {list.length} confirmed and counting toward your recurring charges.{" "}
            {attention.length === 0
              ? "Nothing needs your attention."
              : `${attention.length} still ${attention.length === 1 ? "needs" : "need"} attention.`}{" "}
            <button type="button" className="link" onClick={() => setShowAll(!showAll)} data-testid="toggle-all">
              {showAll ? "Show only what needs attention" : `Show all ${list.length}`}
            </button>
          </p>
          {cleanWaiting > 0 && (
            <p>
              <button type="button" onClick={() => void approveClean()} disabled={approving} data-testid="approve-clean">
                {approving ? "Approving…" : `Approve ${cleanWaiting} statement${cleanWaiting === 1 ? "" : "s"} that passed every check`}
              </button>
            </p>
          )}
          {visible.length > 0 && (
          <>
          <div className="bulk">
            <button type="button" className="link" onClick={toggleAll} data-testid="select-all">
              {allSelected ? "Deselect all" : "Select all"}
            </button>
            <button
              type="button"
              className="link danger"
              onClick={() => void deleteSelected()}
              disabled={selected.size === 0 || deleting}
              data-testid="delete-selected"
            >
              {deleting ? "Deleting…" : `Delete selected${selected.size ? ` (${selected.size})` : ""}`}
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th className="check">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label={allSelected ? "Deselect all statements" : "Select all statements"}
                  />
                </th>
                <th>File</th>
                <th>Account</th>
                <th>Period</th>
                <th>Rows</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr key={s.id} data-testid="statement-row" className={selected.has(s.id) ? "selected" : ""}>
                  <td className="check">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={(e) => toggle(s.id, e.target.checked)}
                      aria-label={`Select ${s.fileName}`}
                    />
                  </td>
                  <td>{s.fileName}</td>
                  <td>
                    {s.issuer ? `${s.issuer} ` : "—"}
                    {s.accountToken && (
                      <button
                        type="button"
                        className="link"
                        title="Name this account (kept on this device only)"
                        onClick={() => {
                          const name = window.prompt("Name for this account (stays on this device)", "");
                          if (name !== null) {
                            setNickname(s.accountToken!, name);
                            setList((l) => (l ? [...l] : l));
                          }
                        }}
                      >
                        {labelFor(s.accountToken)}
                      </button>
                    )}
                  </td>
                  <td>{s.periodStart ? `${s.periodStart} → ${s.periodEnd}` : "—"}</td>
                  <td>{s.transactionCount ?? "—"}</td>
                  <td>
                    <span className={`status ${s.status}`}>
                      {s.status === "confirmed" && s.autoConfirmed ? "Checks passed" : STATUS_LABEL[s.status]}
                    </span>
                    {s.status === "needs_review" && s.problemCount ? ` (${s.problemCount} to check)` : ""}
                    {s.error ? <div className="error small">{s.error}</div> : null}
                  </td>
                  <td className="actions">
                    {(s.status === "needs_review" || s.status === "confirmed") && (
                      <Link to={`/statements/${s.id}`}>{s.status === "confirmed" ? "View" : "Review"}</Link>
                    )}
                    {s.status === "failed" && (
                      <button onClick={() => api.retry(s.id).then(refresh)} type="button">
                        Retry
                      </button>
                    )}
                    <button
                      type="button"
                      className="link danger"
                      onClick={() => {
                        if (confirm(`Delete ${s.fileName}?`)) api.remove(s.id).then(refresh);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
          )}
        </>
      )}
    </section>
  );
}
